/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState } from "react";
import {
  IconDownload,
  IconPlus,
  IconEye,
  IconX,
  IconSearch,
  IconUpload,
} from "@tabler/icons-react";
import ToggleButton from "../../../components/ToggleButton";
import GridView from "../../../components/common/GridView";
import AddStaffModal from "./AddStaffModal";
import {
  useRolesData,
  useStaffCreateMutation,
  useStaffsData,
  useStaffStatusMutation,
  useStaffUpdateMutation,
} from "../../../apis/staffApis";
import useDebounce from "../../../utilities/useDebounce";
import Pagination from "../../../components/common/Pagination";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import MessageModal from "../../../components/MessageModal";
import { useNavigate } from "react-router-dom";
import NoDataFound from "../../../components/common/NoDataFound";
import StaffBulkUploadModals from "./StaffBulkUploadModals";
import StaffBulkResponseModals from "./StaffBulkResponseModals";
import { useSelector } from "react-redux";
import EntriesSelector from "../../../components/common/EntriesSelector";
import { handleDownload } from "../../../utilities/downloadHelper";
import formatDate from "../../../utilities/helper";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { T, useLanguage } from "../../../context/LanguageContext";

type StaffData = {
  id: string;
  roles: string;
  fullName: string;
  phoneNumber: string;
  email: string;
  country: string;
  state: string;
  city: string;
  iqamaNo: string;
  status: string;
  roleName: string;
  roleId: number;
  doc: string;
  countryId: string;
  stateId: string;
  createdAt: string;
};

export default function StaffManagement({
  setHeaderActions,
  setHeaderButtons,
}: {
  setHeaderActions?: (node: React.ReactNode) => void;
  setHeaderButtons?: (node: React.ReactNode) => void;
}) {
  const { t } = useLanguage();
  const [viewMode, setViewMode] = useState<string>(
    () => localStorage.getItem("StaffModeActiveRole") || "list",
  );
  const [showSearch, setShowSearch] = useState(false);

  const [viewStaffModal, setViewStaffModal] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem("StaffModeActiveRole", viewMode);
  }, [viewMode]);

  const [credential, setCredential] = useState<boolean>(false);
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [staffBulkResponse, setStaffBulkResponse] = useState<any>(null);
  const [id, setId] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allSelectedData, setAllSelectedData] = useState<StaffData[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false);

  const credentialToggleModal = () => {
    if (roles?.roles?.length <= 0) {
      alert(t("Please add role before creating staff"));
      return;
    }
    setCredential(!credential);
    if (!credential) {
      reset();
    }
  };
  const [clickedStaffData, setClickedStaffData] = useState<
    StaffData | undefined
  >();

  const { allowed: canCreateStaff, isLoading } = usePermission(
    "staffs",
    "create",
  );
  const { allowed: canUpdateStaff } = usePermission("staffs", "update");

  const {
    // data,
    isSuccess,
    isPending,
    refetch: fetchData,
    isFetching,
  } = useStaffsData(pageNo, limit, searchFor);

  const data = useSelector((state: any) => state?.staff.data);

  // Calculate staff statistics dynamically
  const staffStats = {
    total: data?.total || 0,
    active:
      data?.staffs?.filter((s: any) => s?.status === "ACTIVE").length || 0,
    inactive:
      data?.staffs?.filter((s: any) => s?.status === "INACTIVE").length || 0,
  };

  const { mutate, isPending: isRequestPending } = useStaffCreateMutation();
  const { mutate: staffUpdateMutate, isPending: isStaffUpdateRequestPending } =
    useStaffUpdateMutation();
  const { mutate: statusMutate, isPending: isStatusPending } =
    useStaffStatusMutation(id);
  const debouncedSearchFor = useDebounce(searchFor, 550);
  const { data: roles, refetch: fetchRoles } = useRolesData();
  const [allIds, setAllIds] = useState<any>();
  const getIds = (data: any) => {
    setAllIds(data);
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    control,
    reset,
    formState: { errors },
  } = useForm<any>();

  useEffect(() => {
    fetchData();
  }, [pageNo, limit, debouncedSearchFor]);

  useEffect(() => {
    if (data?.staffs) {
      setAllSelectedData((prev) => {
        const filteredPrev = prev.filter((item) =>
          selectedRows.includes(item.id),
        );
        const newSelected = data.staffs.filter(
          (s: any) =>
            selectedRows.includes(s.id) &&
            !filteredPrev.some((item) => item.id === s.id),
        );
        return [...filteredPrev, ...newSelected];
      });
    }
  }, [data?.staffs, selectedRows]);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  const handleStaffCreate = (data: any) => {
    const mergedWarehouses: string[] = [];

    if (data.warehouse) mergedWarehouses.push(data.warehouse);
    if (Array.isArray(data.assignWarehouse)) {
      mergedWarehouses.push(
        ...data.assignWarehouse.map((warehouse: any) => warehouse?.id),
      );
    } else if (data.assignWarehouse) {
      mergedWarehouses.push(data.assignWarehouse);
    }

    const hasAnyId = Object.values(allIds).some(
      (v) => v !== null && v !== undefined && v !== "",
    );

    let payload: any = {
      ...data,
      type: "LM",
      ...(hasAnyId ? allIds : {}),
      warehouses: mergedWarehouses,
    };

    delete payload.hub;
    delete payload.assignHub;

    payload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v !== undefined),
    );

    mutate(payload, {
      onSuccess: () => {
        setCredential(false);
        toast.success(t("Staff Added Successfully."));
        fetchData();
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          (typeof error === "string" ? error : null) ||
          "Something went wrong";

        toast.error(t(errorMessage));
      },
    });
  };

  useEffect(() => {
    if (id && status) {
      statusMutate(
        { status: status === "ACTIVE" ? "INACTIVE" : "ACTIVE" },
        {
          onSuccess: (data) => {
            fetchData();
            setId("");
            setStatus("");
          },
          onError: (error: any) => {
            console.log(error, "error");
            toast.error(t(`${error}`));
            setId("");
            setStatus("");
          },
        },
      );
    }
  }, [id, status]);

  const navigate = useNavigate();

  const [open, setOpen] = useState<boolean | string>(false);
  const [openbluk, SetOpenBluk] = useState<boolean | string>(false);
  const [openResponse, SetOpenResponse] = useState<boolean | string>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [modalData, setModalData] = useState<{
    id: string;
    status: string;
    open: boolean;
  }>({
    id: "",
    status: "",
    open: false,
  });

  const [columnsData, setColumnsData] = useState([
    { text: "Checkbox", value: "checkbox", isShow: showCheckboxes },
    { text: "S/N", value: "sn", isShow: true },
    { text: "Name", value: "name", isShow: true },
    { text: "Staff Role", value: "staffRole", isShow: true },
    { text: "Mobile No", value: "mobileNo", isShow: true },
    { text: "Email", value: "email", isShow: true },
    { text: "Warehouse", value: "warehouse", isShow: true },
    // { text: "Assigned Warehouse", value: "assignedWarehouse", isShow: true },
    { text: "Date", value: "createdAt", isShow: true },
    { text: "Status", value: "status", isShow: true },
    { text: "Actions", value: "actions", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  // Render stats capsule in the top-right page header
  useEffect(() => {
    if (!setHeaderButtons) return;

    setHeaderButtons(
      <div className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-1.5 rounded-xl border border-slate-200 shadow-sm text-[11px] select-none font-bold min-h-8">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500"><T>Total</T>:</span>
          <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
            {staffStats.total}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-300" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500"><T>Active</T>:</span>
          <span className="text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded">
            {staffStats.active}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-300" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500"><T>Inactive</T>:</span>
          <span className="text-rose-700 bg-rose-50 border border-rose-100 px-1.5 py-0.5 rounded">
            {staffStats.inactive}
          </span>
        </div>
      </div>,
    );

    return () => {
      setHeaderButtons(null);
    };
  }, [
    setHeaderButtons,
    staffStats.total,
    staffStats.active,
    staffStats.inactive,
  ]);

  const handleSelectRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === data?.staffs?.length) {
      setSelectedRows((prev) =>
        prev.filter((id) => !data.staffs.some((s: any) => s.id === id)),
      );
    } else {
      const newSelections =
        data?.staffs
          ?.filter((s: any) => !selectedRows.includes(s.id))
          .map((s: any) => s.id) || [];
      setSelectedRows((prev) => [...prev, ...newSelections]);
    }
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "checkbox":
          return {
            label: (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={
                    data?.staffs?.length > 0 &&
                    data?.staffs?.every((s: any) => selectedRows.includes(s.id))
                  }
                  onChange={handleSelectAll}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                                bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
            render: (s: any) => (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedRows.includes(s.id)}
                  onChange={() => handleSelectRow(s.id)}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                  bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
          };
        case "sn":
          return {
            label: "#",
            render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
          };
        case "name":
          return {
            label: t("Name"),
            render: (s: any) => (
              <div className="flex items-center gap-2 truncate max-w-xs">
                <img
                  src={s?.photo || "/img/dummy-blank-profile.png"}
                  className="w-10 h-10 rounded-full border object-cover"
                  alt={s.fullName}
                />
                <span className="truncate">{s.name}</span>
              </div>
            ),
          };
        case "staffRole":
          return { label: t("Staff Role"), render: (s: any) => t(s.roleName) };
        case "mobileNo":
          return { label: t("Mobile No"), render: (s: any) => s.phone };
        case "email":
          return { label: t("Email"), render: (s: any) => s.email };
        case "warehouse":
          return {
            label: t("Warehouse / Outlet"),
            render: (s: any) => {
              const primary =
                s.warehouseList?.[0]?.name || s.outletList?.[0]?.name;
              return primary || "";
            },
          };
        case "createdAt":
          return {
            label: t("Date"),
            render: (s: any) => formatDate(s.created_at),
          };
        case "status":
          return {
            label: t("Status"),
            render: (s: any) => (
              <div className="flex items-center justify-center">
                <ToggleButton
                  onChange={() => {
                    if (!canUpdateStaff) {
                      toast.error(
                        t("You do not have permission to update staff status"),
                      );
                      return;
                    }
                    setId(s?.id);
                    setStatus(s?.status);
                  }}
                  checked={s?.status === "ACTIVE"}
                />
              </div>
            ),
          };
        case "actions":
          return {
            label: t("Actions"),
            render: (s: any) => (
              <div className="flex items-center gap-2 justify-center">
                <button
                  onClick={() => {
                    setSelectedStaffId(s.id);
                    setViewStaffModal(true);
                  }}
                  className="p-2 border rounded-full bg-[#F7F6F2]"
                >
                  <IconEye size={22} />
                </button>
              </div>
            ),
          };
        default:
          return { label: "", render: () => null };
      }
    });

  const mapDataToExport = (s: any, index: number) => {
    const primaryWarehouse =
      s.warehouseList?.[0]?.name || s.outletList?.[0]?.name || "";

    const hasPrimaryWarehouse = s.warehouseList?.length > 0;
    const assignedRest = hasPrimaryWarehouse
      ? [...(s.warehouseList?.slice(1) || []), ...(s.outletList || [])]
      : [...(s.outletList?.slice(1) || [])];
    const assignedNames = assignedRest.map((i: any) => i.name).join(", ") || "";

    return {
      "S/N": index + 1,
      Name: s.name || s.fullName,
      "Staff Role": s.roleName,
      "Mobile No": s.phone || s.phoneNumber,
      Email: s.email,
      "Warehouse / Outlet": primaryWarehouse,
      Date: formatDate(s.created_at),
      Status: s.status,
    };
  };

  return (
    <>
      <div className="">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Left side: Brand badge */}
          <div className="flex flex-wrap items-center gap-3"></div>

          {/* Right side: Search + Export + Entries + Stats capsule */}
          <div className="flex items-center flex-wrap gap-2.5">
            {/* Sliding Search */}
            <div className="relative flex items-center">
              <div
                onClick={() => {
                  if (!showSearch) {
                    setShowSearch(true);
                  }
                }}
                className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-md bg-white shadow-sm h-8 ${
                  showSearch
                    ? "w-64 px-2 cursor-default"
                    : "w-8 justify-center cursor-pointer"
                }`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSearch(!showSearch);
                  }}
                  className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                  title={t("Search")}
                >
                  <IconSearch size={17} stroke={2.5} />
                </button>
                {showSearch && (
                  <>
                    <input
                      type="text"
                      value={searchFor}
                      onChange={(e) => {
                        setSearchFor(e.target.value);
                        setPageNo(1);
                      }}
                      placeholder={t("Search...")}
                      autoFocus
                      className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none focus:outline-none focus:ring-0"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchFor("");
                        setShowSearch(false);
                        setPageNo(1);
                      }}
                      className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                      title={t("Clear & Close")}
                    >
                      <IconX size={14} stroke={2.5} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Export to Excel */}
            <button
              onClick={() =>
                handleDownload({
                  selectedRows,
                  allSelectedData,
                  setSelectedRows,
                  setAllSelectedData,
                  showCheckboxes,
                  setShowCheckboxes,
                  setColumnsData,
                  mapDataToExport,
                  fileName: "staff_management.xlsx",
                })
              }
              className={`flex items-center justify-center h-8 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-md transition shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 duration-150 ${
                showCheckboxes && selectedRows.length === 0
                  ? "bg-white/50 text-slate-400"
                  : "bg-white hover:bg-slate-50"
              } ${selectedRows.length > 0 ? "px-3" : "w-8"}`}
              title={t("Export to Excel")}
            >
              <IconDownload size={17} stroke={2.5} />
              {selectedRows.length > 0 && (
                <span className="font-bold text-xs ml-1">
                  ({selectedRows.length})
                </span>
              )}
            </button>

            {data && data.totalPages * limit > 10 && (
              <EntriesSelector
                value={limit}
                options={[
                  10,
                  ...(data.totalPages * limit > 10 ? [20] : []),
                  ...(data.totalPages * limit > 20 ? [30] : []),
                  ...(data.totalPages * limit > 50 ? [50] : []),
                  ...(data.totalPages * limit > 100 ? [100] : []),
                ]}
                onChange={(val) => {
                  setLimit(val);
                  setPageNo(1);
                  setSelectedRows([]);
                  setAllSelectedData([]);
                }}
              />
            )}

            {/* Add + Bulk Upload buttons */}
            {canCreateStaff && (
              <>
                <button
                  onClick={() =>
                    window.dispatchEvent(new CustomEvent("openAddStaffModal"))
                  }
                  className="flex items-center justify-center p-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-xl transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 duration-150"
                  title={t("Add Staff")}
                >
                  <IconPlus size={17} stroke={2.5} />
                </button>
                <button
                  onClick={() =>
                    window.dispatchEvent(new CustomEvent("openBulkUploadModal"))
                  }
                  className="flex items-center justify-center p-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-xl transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 duration-150"
                  title={t("Bulk Upload")}
                >
                  <IconUpload size={17} stroke={2.5} />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="py-4">
          {data?.staffs && data.staffs.length > 0 ? (
            <GridView
              columns={5}
              className="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              data={data?.staffs}
              renderItem={(staff: any) => (
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

                  {/* Header */}
                  <div className="flex items-start justify-between border-b border-[#D2D2D2]/70">
                    <div>
                      {(staff.staffCode || staff.code) && (
                        <p className="text-[11px] text-[#212121] uppercase tracking-wider">
                          {staff.staffCode || staff.code}
                        </p>
                      )}

                      <h3 className="text-[#000000] font-semibold text-lg mt-1 line-clamp-1">
                        {staff.name}
                      </h3>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setId(staff?.id);
                        setStatus(staff?.status);
                      }}
                      className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer ${
                        staff.status === "ACTIVE"
                          ? "bg-green-500/15 text-green-400"
                          : "bg-red-500/15 text-red-400"
                      }`}
                    >
                      <T>{staff.status === "ACTIVE" ? "Active" : "Inactive"}</T>
                    </button>
                  </div>

                  <div className="space-y-3">
                    {/* Role */}
                    <div className="flex items-center justify-between">
                      <span className="text-[#212121] text-sm"><T>Role</T>:</span>
                      <span className="text-[#212121] text-sm font-medium">
                        {t(staff.roleName) || "-"}
                      </span>
                    </div>

                    {/* Phone */}
                    <div className="flex items-center justify-between">
                      <span className="text-[#212121] text-sm"><T>Phone</T>:</span>
                      <span className="text-[#212121] text-sm font-medium">
                        {staff.phone || "-"}
                      </span>
                    </div>

                    {/* Email */}
                    <div className="flex items-center justify-between">
                      <span className="text-[#212121] text-sm"><T>Email</T>:</span>
                      <span
                        className="text-[#212121] text-sm font-medium truncate max-w-[160px]"
                        title={staff.email}
                      >
                        {staff.email || "-"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      className="flex-1 h-10 rounded-lg bg-[#FBD037]/20  text-[#000000] text-sm font-medium "
                      onClick={(e) => {
                        e.stopPropagation();
                        window.dispatchEvent(
                          new CustomEvent("openStaffModal", {
                            detail: {
                              staffId: staff.id,
                            },
                          }),
                        );
                      }}
                    >
                      <T>View Profile</T>
                    </button>
                  </div>
                </div>
              )}
            />
          ) : (
            <NoDataFound />
          )}
          
        </div>
      </div>

      {/* {openbluk && (
        <StaffBulkUploadModals
          onClose={() => SetOpenBluk(false)}
          onResponseOpene={() => SetOpenResponse(true)}
          setStaffBulkResponse={setStaffBulkResponse}
        />
      )}

      {openResponse && staffBulkResponse && (
        <StaffBulkResponseModals
          data={staffBulkResponse}
          onClose={() => SetOpenResponse(false)}
        />
      )} */}
    </>
  );
}
